import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "5511917611575";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de agendar uma consulta jurídica.";
const MAPS_LINK = "https://www.google.com/maps/search/Rod+Presidente+Tancredo+de+Almeida+Neves+2134+Caieiras+SP";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";

const caseTypeLabels = {
  trabalhista: "Direito do Trabalho",
  tributario: "Direito Tributário",
  imobiliario: "Direito Imobiliário",
  previdenciario: "Direito Previdenciário",
  civel: "Direito Cível",
  familia: "Direito de Família",
  outro: "Outro",
};

const ContatoPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    case_type: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsAppLink, setShowWhatsAppLink] = useState(false);
  const [whatsAppUrl, setWhatsAppUrl] = useState("");

  const generateWhatsAppUrl = () => {
    const areaLabel = caseTypeLabels[formData.case_type] || formData.case_type;
    let message = `Olá! Gostaria de agendar uma consulta jurídica.\n\n`;
    message += `*Dados do Contato:*\n`;
    message += `Nome: ${formData.name}\n`;
    message += `Telefone: ${formData.phone}\n`;
    message += `Área: ${areaLabel}\n`;
    if (formData.description) {
      message += `\n*Resumo do Caso:*\n${formData.description}`;
    }
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.case_type) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Mensagem registrada com sucesso!");
      
      const url = generateWhatsAppUrl();
      setWhatsAppUrl(url);
      setShowWhatsAppLink(true);
      
    } catch (error) {
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contato | Helio Lopes Leal Advocacia</title>
        <meta name="description" content="Entre em contato com o escritório Helio Lopes Leal Advocacia. WhatsApp, telefone e formulário." />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-blue-900 font-medium tracking-wider text-sm mb-4">CONTATO</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Entre em <span className="text-blue-900">Contato</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Estamos prontos para atender você. Escolha a forma de contato mais conveniente 
              ou preencha o formulário abaixo.
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Form */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Envie sua Mensagem
                </h2>
                <div className="h-1 w-16 bg-blue-900 mb-6"></div>
                <p className="text-gray-600 mb-8">
                  Preencha o formulário para uma análise do seu caso. 
                  Suas informações são protegidas e o contato é confidencial.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo *</label>
                    <Input
                      type="text"
                      placeholder="Seu nome"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone (WhatsApp) *</label>
                    <Input
                      type="tel"
                      placeholder="(11) 99999-9999"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Área de Interesse *</label>
                    <Select
                      value={formData.case_type}
                      onValueChange={(value) => setFormData({ ...formData, case_type: value })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Selecione a área" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trabalhista">Direito do Trabalho</SelectItem>
                        <SelectItem value="tributario">Direito Tributário</SelectItem>
                        <SelectItem value="imobiliario">Direito Imobiliário</SelectItem>
                        <SelectItem value="previdenciario">Direito Previdenciário</SelectItem>
                        <SelectItem value="civel">Direito Cível</SelectItem>
                        <SelectItem value="familia">Direito de Família</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mensagem</label>
                    <Textarea
                      placeholder="Descreva brevemente sua situação..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="min-h-[120px]"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-blue-900 hover:bg-blue-800 text-white font-medium"
                  >
                    {isSubmitting ? (
                      <span>Enviando...</span>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Mensagem
                      </>
                    )}
                  </Button>

                  {/* Link do WhatsApp após envio */}
                  {showWhatsAppLink && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
                      <div className="flex items-center gap-2 text-green-800">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Mensagem registrada! Clique para enviar pelo WhatsApp:</span>
                      </div>
                      <a
                        href={whatsAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded font-medium transition-all w-full"
                        onClick={() => {
                          setShowWhatsAppLink(false);
                          setFormData({ name: "", phone: "", case_type: "", description: "" });
                        }}
                      >
                        <MessageCircle className="w-5 h-5" />
                        Abrir WhatsApp com Resumo do Caso
                      </a>
                    </div>
                  )}

                  <p className="text-xs text-gray-500 text-center">
                    Informações protegidas e sigilo garantido.
                  </p>
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <div className="bg-gray-50 rounded-xl p-8 mb-8">
                  <h3 className="text-xl font-bold mb-6 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Informações de Contato
                  </h3>

                  <div className="space-y-6">
                    <a
                      href={WHATSAPP_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 text-gray-700 hover:text-green-600 transition-colors"
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center text-green-600">
                        <MessageCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium">WhatsApp</p>
                        <p className="text-gray-500">(11) 91761-1575</p>
                      </div>
                    </a>

                    <a
                      href="tel:+5511917611575"
                      className="flex items-center gap-4 text-gray-700 hover:text-blue-900 transition-colors"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-900">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium">Telefone</p>
                        <p className="text-gray-500">(11) 91761-1575</p>
                      </div>
                    </a>

                    <a
                      href="mailto:h.lopesleal@adv.oabsp.org.br"
                      className="flex items-center gap-4 text-gray-700 hover:text-blue-900 transition-colors"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-900">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium">E-mail</p>
                        <p className="text-gray-500">h.lopesleal@adv.oabsp.org.br</p>
                      </div>
                    </a>

                    <a
                      href={MAPS_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-4 text-gray-700 hover:text-blue-900 transition-colors"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-900 flex-shrink-0">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium">Endereço</p>
                        <p className="text-gray-500 text-sm">
                          Rod. Pres. Tancredo de Almeida Neves, nº 2134, Sala 01
                          <br />
                          Caieiras/SP
                          <br />
                          <span className="text-xs">(Próximo ao Supermercados Federzoni - Serpa)</span>
                        </p>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Map */}
                <div className="bg-white rounded-xl overflow-hidden shadow-sm mb-8">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3658.8!2d-46.74!3d-23.36!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94cef6a5a5a5a5a5%3A0x0!2sRod.%20Pres.%20Tancredo%20de%20Almeida%20Neves%2C%202134%20-%20Caieiras%2C%20SP!5e0!3m2!1spt-BR!2sbr!4v1"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização do Escritório"
                  ></iframe>
                </div>

                {/* Quick WhatsApp */}
                <div className="bg-green-50 rounded-xl p-8 border border-green-100">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Atendimento Rápido</h4>
                  <p className="text-gray-600 mb-6 text-sm">
                    Clique no botão abaixo para iniciar uma conversa diretamente pelo WhatsApp.
                  </p>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded font-medium transition-all w-full"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Iniciar Conversa no WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ContatoPage;
